import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TelemetryPanel } from '../../telemetry/components/TelemetryPanel/TelemetryPanel.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionOutcome,
  ActionScenario,
  ActionScenarioType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';

import {
  buildTrajectoryLink,
  isTrajectoryRequest,
} from './trajectoryRequest.js';

export interface ReportFrameProps {
  html: string;
  title: string;
  className?: string;
}

type Handoff =
  | { kind: 'preparing' }
  | { kind: 'ready'; url: string }
  | { kind: 'error'; message: string };

export function ReportFrame({ html, title, className }: ReportFrameProps) {
  const frame = useRef<HTMLIFrameElement>(null);
  const busy = useRef(false);
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const { recordAction } = useUserBi();
  const record = useCallback(
    (dismiss: boolean) =>
      recordAction({
        panelType: PanelType.ReportTrajectory,
        panelRegion: PanelRegion.Main,
        actionScenario: dismiss
          ? ActionScenario.DismissReportTrajectory
          : ActionScenario.OpenReportTrajectory,
        actionScenarioType: dismiss
          ? ActionScenarioType.CloseDialog
          : ActionScenarioType.Open,
        actionGesture: 'click',
        actionOutcome: ActionOutcome.Requested,
      }),
    [recordAction],
  );

  useEffect(() => {
    let active = true;
    let responsePort: MessagePort | undefined;
    busy.current = false;
    setHandoff(null);

    const receive = (event: MessageEvent<unknown>) => {
      if (
        event.source !== frame.current?.contentWindow ||
        !isTrajectoryRequest(event.data)
      ) {
        return;
      }
      const port = event.ports[0];
      if (busy.current) {
        port?.postMessage({
          status: 'error',
          message: 'Dismiss the current trajectory confirmation first.',
        });
        port?.close();
        return;
      }
      if (!port || event.ports.length !== 1) {
        for (const extraPort of event.ports) extraPort.close();
        setHandoff({
          kind: 'error',
          message: 'The report sent an invalid trajectory response channel.',
        });
        return;
      }
      busy.current = true;
      responsePort = port;
      setHandoff({ kind: 'preparing' });
      void (async () => {
        let tab: Window | null = null;
        try {
          const url = await buildTrajectoryLink(event.data);
          if (!active) return;
          if (navigator.userActivation?.isActive)
            tab = window.open('about:blank', '_blank');
          if (tab) {
            // Keep a handle to detect blocking, then isolate the trusted blank tab
            // before any destination content can load.
            tab.opener = null;
            const link = tab.document.createElement('a');
            link.href = url;
            link.rel = 'noreferrer';
            link.referrerPolicy = 'no-referrer';
            tab.document.body.append(link);
            link.click();
            link.remove();
            tab = null;
            busy.current = false;
            setHandoff(null);
            record(false);
          } else {
            setHandoff({ kind: 'ready', url });
          }
          port.postMessage({ status: 'ready' });
        } catch (error) {
          tab?.close();
          if (!active) return;
          const message =
            error instanceof Error
              ? error.message
              : 'The trajectory link could not be prepared.';
          setHandoff({ kind: 'error', message });
          port.postMessage({ status: 'error', message });
        } finally {
          port.close();
          responsePort = undefined;
        }
      })();
    };
    window.addEventListener('message', receive);
    return () => {
      active = false;
      busy.current = false;
      responsePort?.close();
      window.removeEventListener('message', receive);
    };
  }, [html, record]);

  return (
    <>
      {handoff && handoff.kind !== 'preparing' && (
        <TelemetryPanel
          panelType={PanelType.ReportTrajectory}
          panelRegion={PanelRegion.Main}
        >
          <MessageBar intent={handoff.kind === 'error' ? 'error' : 'info'}>
            <MessageBarBody>
              <MessageBarTitle>Trajectory</MessageBarTitle>
              {handoff.kind === 'error' ? (
                <span role="alert">{handoff.message}</span>
              ) : (
                <>
                  Your browser did not open a new tab. Open the trajectory here
                  instead.{' '}
                  <Button
                    as="a"
                    href={handoff.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    appearance="primary"
                    onClick={() => record(false)}
                  >
                    Open trajectory in AI Studio
                  </Button>
                </>
              )}{' '}
              <Button
                onClick={() => {
                  record(true);
                  busy.current = false;
                  setHandoff(null);
                }}
              >
                Dismiss
              </Button>
            </MessageBarBody>
          </MessageBar>
        </TelemetryPanel>
      )}
      <iframe
        ref={frame}
        title={title}
        className={className}
        srcDoc={html}
        sandbox="allow-scripts"
        onLoad={() => {
          // The frame has an opaque origin. This greeting contains no evidence.
          frame.current?.contentWindow?.postMessage(
            { type: 'evalworkbench:trajectory-host', version: 1 },
            '*',
          );
        }}
      />
    </>
  );
}
